// src/oura/visibility/useScanQuestions.ts
//
// The questions of earlier scans, for the Question history diff and the change
// banner. The history carries only aggregates, so each scan's questions come
// from `GET /assessments/{id}`, through the same builder the screen uses, so a
// past scan's "named" is counted exactly like today's.
//
// A scan never changes once complete, so each one is fetched once per page
// load and shared by every caller. The scan on screen is never fetched: it is
// read off the inputs and model the page already holds. A sample business
// carries its earlier scans in the fixture (`questionSets`).

import { useEffect, useMemo, useState } from "react";
import { getAssessment, type AeoScoreHistoryEntry } from "@service/aeo";
import { buildVisModel, visibilityInputsFromAssessment, type LatestAssessment, type VisibilityInputs } from "./fromLive";
import type { VisModel } from "./model";
import type { ScanQuestions } from "./questionSets";

/** The scan on screen, from what the page already built. */
export function scanQuestionsFrom(inputs: VisibilityInputs, model: VisModel | null): ScanQuestions {
    return {
        assessmentId: inputs.assessmentId ?? "",
        capturedAt: inputs.capturedAt,
        queries: inputs.queries,
        named: model ? Object.fromEntries(model.rows.map((r) => [r.key, r.named])) : null,
    };
}

const loaded = new Map<string, Promise<ScanQuestions | null>>();

function load(assessmentId: string, capturedAt: string, history: AeoScoreHistoryEntry[]): Promise<ScanQuestions | null> {
    const cached = loaded.get(assessmentId);
    if (cached) return cached;
    const p = getAssessment(assessmentId)
        .then((a) => {
            const past = visibilityInputsFromAssessment(a as unknown as LatestAssessment, { capturedAt, history });
            return scanQuestionsFrom({ ...past, assessmentId }, past.vis ? buildVisModel(past, null) : null);
        })
        // A failed read is not cached, so the next open asks again.
        .catch(() => {
            loaded.delete(assessmentId);
            return null;
        });
    loaded.set(assessmentId, p);
    return p;
}

/**
 * By assessment id: the scan's questions, `undefined` while loading, `null`
 * when it could not be read. Only while `enabled`.
 */
export function useScanQuestions({
    ids,
    inputs,
    model,
    fixture,
    enabled,
}: {
    ids: string[];
    inputs: VisibilityInputs | null;
    model: VisModel | null;
    fixture: boolean;
    enabled: boolean;
}): Record<string, ScanQuestions | null | undefined> {
    const key = Array.from(new Set(ids)).sort().join(",");
    const [got, setGot] = useState<Record<string, ScanQuestions | null>>({});
    const onScreen = useMemo(() => (inputs ? scanQuestionsFrom(inputs, model) : null), [inputs, model]);

    useEffect(() => {
        if (!enabled || !inputs || fixture) return;
        let live = true;
        const wanted = key ? key.split(",") : [];
        wanted
            .filter((id) => id !== inputs.assessmentId)
            .forEach((id) => {
                const entry = inputs.history.find((h) => h.assessment_id === id);
                void load(id, entry?.captured_at ?? "", inputs.history).then((s) => {
                    if (live) setGot((prev) => ({ ...prev, [id]: s }));
                });
            });
        return () => { live = false; };
        // `inputs` is memoised by the screen, so it changes only with the scan
        // or its history; a change re-runs this and drops the older callbacks.
    }, [enabled, fixture, key, inputs]);

    const out: Record<string, ScanQuestions | null | undefined> = {};
    (key ? key.split(",") : []).forEach((id) => {
        if (onScreen && id === inputs?.assessmentId) out[id] = onScreen;
        else if (fixture) out[id] = inputs?.questionSets?.[id] ?? null;
        else out[id] = got[id];
    });
    return out;
}
